import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import styles from './lightbox.module.scss';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import type {LightboxProps} from '@Components/lightbox/lightbox.types';
import {MODAL_DEFAULT_Z_INDEX} from '@Components/modal';
import {isBackgroundClick} from './lightbox.library';

const DEFAULT_Z_INDEX = MODAL_DEFAULT_Z_INDEX - 1;

export function Lightbox({isOpen, onClose, children, zIndex = DEFAULT_Z_INDEX, usePortal = true, isPartOfBackground}: LightboxProps): ReactElement | null {
  const [isVisible, setIsVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const pressTargetRef = useRef<EventTarget | null>(null);

  useEffect(() => {
    setMounted(true);
    return (): void => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const isBackground = (target: EventTarget): boolean => {
    return target === contentRef.current || target === innerRef.current || (isPartOfBackground?.(target) ?? false);
  };

  const closeOnBackgroundClick = (clickTarget: EventTarget): void => {
    const isClose = isBackgroundClick({pressTarget: pressTargetRef.current, clickTarget, isBackground});
    pressTargetRef.current = null;

    if (isClose) {
      onClose();
    }
  };

  const lightboxContent = (
    <>
      <ClickableDiv className={`${styles.lightboxOverlay} ${isVisible ? styles.visible : ''}`} style={{zIndex: zIndex}} onClick={onClose} />
      <ClickableDiv
        className={`${styles.lightboxContent} ${isVisible ? styles.visible : ''}`}
        ref={contentRef}
        style={{zIndex: zIndex + 1}}
        onPointerDown={(e): void => {
          pressTargetRef.current = e.target;
        }}
        onClick={(e): void => {
          closeOnBackgroundClick(e.target);
        }}
      >
        <div ref={innerRef} className={styles.lightboxContentInner}>
          {children}
        </div>
      </ClickableDiv>
    </>
  );

  if (usePortal && mounted && typeof document !== 'undefined') {
    return createPortal(lightboxContent, document.body);
  }

  return lightboxContent;
}
